import React, { FC, useContext, useState, useCallback } from 'react';
import { debounce } from 'lodash';
import { useDeepCompareEffect } from '@oscar/utils';
import { useDispatch, useSelector } from 'react-redux';
import { validateRequest } from '@oscar/request';
import { FieldProps } from './Field';
import CustomField from './CustomField';
import { preloadData } from '../reducer';
import { getDataByKeySelector } from '../selectors';
import { AsyncFieldProps, RequestAction } from '../typed';
import { FormContext } from '../formContext';

interface AsyncProps extends AsyncFieldProps {}

const AsyncField: FC<AsyncProps> = React.memo(({ children, ...props }) => {
  const context = useContext(FormContext);
  const dispatch = useDispatch();
  const data = useSelector((state) =>
    getDataByKeySelector(context.options!.name)(state, props.name)
  );

  const [requests, setRequests] = useState<RequestAction[]>([]);

  const requestData = (data) => {
    const _requests = props.requests
      .map((request) => ({
        metadata: {
          form: context.options!.name,
        },
        request: validateRequest<AsyncProps>(
          { ...request, name: `${props.name}.${request.name}` },
          data
        ),
      }))
      .filter((newRequest) => newRequest.request.shouldRequest);

    return _requests;
  };

  useDeepCompareEffect(() => {
    setRequests(requestData(props));
  }, [requestData(props), props.otherFields]);

  const reloadData = useCallback(
    (customData: Record<string, any>) => {
      setRequests(requestData({ ...props, customData }));
    },
    [props.requests]
  );

  const fetchData = useCallback(
    debounce((requests) => {
      dispatch(preloadData.action(requests));
    }, 1000),
    [requests]
  );

  useDeepCompareEffect(() => {
    if (requests) {
      fetchData(requests);
    }
  }, [requests]);

  return typeof children === 'function'
    ? children({
        ...props,
        requests: requests.map((actionRequest) => actionRequest.request),
        reloadData,
        data: data || undefined,
      })
    : null;
});

export default (props: FieldProps & AsyncFieldProps) => (
  <CustomField<AsyncFieldProps> {...props} component={AsyncField} />
);
